import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, of, switchMap } from 'rxjs';
import {
  BusinessService,
  BusinessLocationScope,
  BusinessStore,
  BusinessSubscription,
  CreateBusinessStoreRequest,
} from '../../core/services/business.service';
import { GeocodeResult, LocationService, LocationSuggestion } from '../../core/services/location.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { TokenService } from '../../core/auth/token.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { BusinessLocationContextService } from '../../core/services/business-location-context.service';
import { UsersService } from '../../core/services/users.service';
import { AuthService } from '../../core/auth/auth.service';
import { CurrentUserService } from '../../core/auth/current-user.service';

type EditableLocationField = 'area' | 'city' | 'state' | 'country' | 'postalCode';

@Component({
  selector: 'app-stores',
  imports: [FormsModule],
  templateUrl: './stores.html',
  styleUrl: './stores.scss',
})
export class Stores implements OnInit, OnDestroy {
  private readonly businessService = inject(BusinessService);
  private readonly locationService = inject(LocationService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly tokens = inject(TokenService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly locationContext = inject(BusinessLocationContextService);
  private readonly users = inject(UsersService);
  private readonly auth = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmation = inject(ConfirmationService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly addressSearchTerms = new Subject<string>();
  private readonly subscriptions = new Subscription();

  stores: BusinessStore[] = [];
  subscription?: BusinessSubscription;
  suggestions: LocationSuggestion[] = [];
  addressSearch = '';
  selectedLocation?: GeocodeResult;
  mapPreviewUrl?: SafeResourceUrl;
  isLoading = false;
  isSearching = false;
  isResolvingLocation = false;
  isLocating = false;
  isSubmitting = false;
  busyStoreId = '';
  errorMessage = '';
  successMessage = '';
  submitErrorMessage = '';
  storeListErrorMessage = '';
  hasTenantContext = false;
  editableLocationFields = new Set<EditableLocationField>();

  form: CreateBusinessStoreRequest = this.emptyForm();

  get isPlatformPortal(): boolean {
    return this.router.url.startsWith('/admin');
  }

  get locationScope(): BusinessLocationScope {
    return this.isPlatformPortal ? 'platform' : 'business';
  }

  get canCreateLocation(): boolean { return this.currentUser.hasPermission('LOCATION_CREATE'); }
  get canSetPrimaryLocation(): boolean { return this.currentUser.hasPermission('LOCATION_SET_PRIMARY'); }
  get canDeactivateLocation(): boolean { return this.currentUser.hasPermission('LOCATION_DEACTIVATE'); }

  ngOnInit(): void {
    this.hasTenantContext = this.isPlatformPortal || Boolean(this.tokens.getTenantId());
    if (this.hasTenantContext) {
      this.loadStores();
      this.loadSubscription();
    } else {
      this.storeListErrorMessage =
        'This business account is not linked to a tenant. Link the account, then sign out and sign in again.';
    }
    this.watchAddressSearch();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get canCreateStore(): boolean {
    return this.hasTenantContext && (this.subscription?.canCreateStore ?? true);
  }

  get isFormReady(): boolean {
    return Boolean(
      this.form.name.trim() &&
        this.form.addressLine1 &&
        this.form.city &&
        this.form.state &&
        this.form.latitude !== undefined &&
        this.form.longitude !== undefined &&
        this.canCreateStore,
    );
  }

  loadStores(forceRefresh = false): void {
    if (!this.hasTenantContext && !this.isPlatformPortal) {
      this.storeListErrorMessage =
        'This business account is not linked to a tenant. Link the account, then sign out and sign in again.';
      return;
    }

    this.isLoading = true;
    this.storeListErrorMessage = '';
    this.businessService
      .stores(this.locationScope, forceRefresh)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.refreshView();
        }),
      )
      .subscribe({
        next: (stores) => {
          this.stores = stores;
          this.isLoading = false;
          this.refreshView();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.storeListErrorMessage = httpErrorMessage(error, 'Unable to load business locations.');
          this.refreshView();
        },
      });
  }

  private loadSubscription(): void {
    if (this.isPlatformPortal) {
      return;
    }

    this.businessService.subscription().subscribe({
        next: (subscription) => {
          this.subscription = subscription;
          this.refreshView();
        },
        error: () => {
          this.subscription = undefined;
          this.refreshView();
      },
    });
  }

  searchAddress(): void {
    const input = this.addressSearch.trim();
    if (input.length < 3) {
      this.suggestions = [];
      this.errorMessage = 'Enter at least 3 characters to search for an address.';
      return;
    }

    this.fetchSuggestions(input);
  }

  onAddressSearchChange(value: string): void {
    const input = value.trim();
    if (this.selectedLocation && value !== this.selectedLocation.formattedAddress) {
      this.clearResolvedLocation();
    }

    if (input.length < 3) {
      this.suggestions = [];
      return;
    }

    this.addressSearchTerms.next(input);
  }

  selectSuggestion(suggestion: LocationSuggestion): void {
    this.addressSearch = suggestion.description;
    this.suggestions = [];
    this.isResolvingLocation = true;
    this.errorMessage = '';
    this.locationService
      .placeDetails(suggestion.placeId)
      .pipe(
        finalize(() => {
          this.isResolvingLocation = false;
          this.refreshView();
        }),
      )
      .subscribe({
        next: (location) => this.applyLocation(location),
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to load the selected location details.');
          this.refreshView();
        },
      });
  }

  useCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.errorMessage = 'Location is not supported in this browser.';
      return;
    }

    this.isLocating = true;
    this.errorMessage = '';
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        this.locationService
          .reverseGeocode(coords.latitude, coords.longitude)
          .pipe(
            finalize(() => {
              this.isLocating = false;
              this.refreshView();
            }),
          )
          .subscribe({
            next: (location) => this.applyLocation(location),
            error: (error: unknown) => {
              this.errorMessage = httpErrorMessage(error, 'Unable to resolve your current address.');
              this.refreshView();
            },
          });
      },
      () => {
        this.isLocating = false;
        this.errorMessage = 'Location permission was denied or your position is unavailable.';
        this.refreshView();
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  createStore(): void {
    if (this.form.phone && !/^\d{10}$/.test(this.form.phone)) {
      this.submitErrorMessage = 'Enter a valid 10-digit phone number.';
      this.errorMessage = this.submitErrorMessage;
      return;
    }
    if (!this.isFormReady) {
      this.submitErrorMessage = 'Choose a complete address from the map search before creating the business location.';
      this.errorMessage = this.submitErrorMessage;
      this.cdr.detectChanges();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.submitErrorMessage = '';
    this.successMessage = '';
    this.businessService
      .createStore(this.form, this.locationScope)
      .pipe(switchMap((store) => this.isPlatformPortal
        ? of(store)
        : this.users.assignMyBusinessLocation(store.id).pipe(
            switchMap(() => this.auth.refreshSession()),
            switchMap(() => of(store)),
          )))
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (store) => {
          const existingStores = this.stores
            .filter((item) => item.id !== store.id)
            .map((item) => (store.isPrimary ? { ...item, isPrimary: false } : item));
          this.stores = [store, ...existingStores];
          if (this.subscription) {
            this.subscription = {
              ...this.subscription,
              activeStores: this.subscription.activeStores + 1,
              canCreateStore: this.subscription.activeStores + 1 < this.subscription.maxStores,
            };
          }
          this.successMessage = 'Business location created. It is now available when creating a product.';
          if (!this.isPlatformPortal) this.locationContext.select(store.id);
          this.resetForm();
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
          if (returnUrl?.startsWith('/business/')) {
            setTimeout(() => void this.router.navigateByUrl(returnUrl), 350);
          }
        },
        error: (error: unknown) => {
          this.submitErrorMessage = httpErrorMessage(error, 'Unable to create business location.');
          this.errorMessage = this.submitErrorMessage;
          this.isSubmitting = false;
          this.cdr.detectChanges();
        },
      });
  }
  phoneInput(value:string):void { this.form.phone=value.replace(/\D/g,'').slice(0,10); }

  setPrimary(store: BusinessStore): void {
    this.busyStoreId = store.id;
    this.errorMessage = '';
    this.businessService
      .setPrimaryStore(store.id, this.locationScope)
      .pipe(
        finalize(() => {
          this.busyStoreId = '';
          this.refreshView();
        }),
      )
      .subscribe({
        next: () => {
          this.stores = this.stores.map((item) => ({ ...item, isPrimary: item.id === store.id }));
          this.successMessage = `${store.name} is now the primary business location.`;
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to set the primary business location.');
        },
      });
  }

  async deactivate(store: BusinessStore): Promise<void> {
    const confirmed = await this.confirmation.confirmDestructive(
      `Deactivate ${store.name}?`,
      'This location will no longer be available for new product listings.',
      'Deactivate location',
    );
    if (!confirmed) {
      return;
    }

    this.busyStoreId = store.id;
    this.errorMessage = '';
    this.refreshView();
    this.businessService
      .deactivateStore(store.id, this.locationScope)
      .pipe(
        finalize(() => {
          this.busyStoreId = '';
          this.refreshView();
        }),
      )
      .subscribe({
        next: () => {
          this.stores = this.stores.filter((item) => item.id !== store.id);
          this.loadStores();
          this.successMessage = `${store.name} was deactivated.`;
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to deactivate the business location.');
        },
      });
  }

  storeAddress(store: BusinessStore): string {
    const addressLine1 = this.stripStructuredAddressParts(store.addressLine1 ?? '', [
      store.area,
      store.city,
      store.state,
      store.postalCode,
      store.country,
    ]);
    return this.uniqueAddressParts([
      store.addressLine2,
      addressLine1 || store.addressLine1,
      store.area,
      store.city,
      store.state,
      store.postalCode,
      store.country,
    ]).join(', ');
  }

  isLocationFieldEditable(field: EditableLocationField): boolean {
    return this.editableLocationFields.has(field);
  }

  private watchAddressSearch(): void {
    this.subscriptions.add(
      this.addressSearchTerms
        .pipe(
          debounceTime(120),
          distinctUntilChanged(),
          switchMap((input) => {
            this.isSearching = true;
            this.errorMessage = '';
            return this.locationService.autocomplete(input).pipe(
              catchError((error: unknown) => {
                this.errorMessage = httpErrorMessage(error, 'Address search is unavailable.');
                return of([] as LocationSuggestion[]);
              }),
              finalize(() => {
                this.isSearching = false;
              }),
            );
          }),
        )
        .subscribe((suggestions) => {
          this.suggestions = suggestions;
          this.refreshView();
        }),
    );
  }

  private fetchSuggestions(input: string): void {
    this.isSearching = true;
    this.errorMessage = '';
    this.locationService
      .autocomplete(input)
      .pipe(
        finalize(() => {
          this.isSearching = false;
          this.refreshView();
        }),
      )
      .subscribe({
        next: (suggestions) => {
          this.suggestions = suggestions;
          if (!suggestions.length) {
            this.errorMessage = 'No matching address found.';
          }
        },
        error: (error: unknown) => {
          this.suggestions = [];
          this.errorMessage = httpErrorMessage(error, 'Address search is unavailable.');
        },
      });
  }

  private applyLocation(location: GeocodeResult): void {
    const postalCode = location.postalCode ?? location.zipCode ?? '';
    this.editableLocationFields = new Set(
      ([
        ['area', location.area],
        ['city', location.city],
        ['state', location.state],
        ['country', location.country],
        ['postalCode', postalCode],
      ] as Array<[EditableLocationField, string | null | undefined]>)
        .filter(([, value]) => !value?.trim())
        .map(([field]) => field),
    );
    this.selectedLocation = location;
    this.addressSearch = location.formattedAddress;
    this.form = {
      ...this.form,
      addressLine1: this.locationAddressLine1(location, postalCode),
      area: location.area ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      country: location.country ?? '',
      postalCode,
      latitude: Number(location.latitude.toFixed(6)),
      longitude: Number(location.longitude.toFixed(6)),
    };
    const mapUrl = `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=16&output=embed`;
    this.mapPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);
    this.errorMessage = '';
    this.refreshView();
  }

  private resetForm(): void {
    this.form = this.emptyForm();
    this.addressSearch = '';
    this.selectedLocation = undefined;
    this.mapPreviewUrl = undefined;
    this.suggestions = [];
    this.editableLocationFields.clear();
    this.submitErrorMessage = '';
  }

  private clearResolvedLocation(): void {
    this.selectedLocation = undefined;
    this.mapPreviewUrl = undefined;
    this.form = {
      ...this.form,
      addressLine1: '',
      area: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      latitude: undefined,
      longitude: undefined,
    };
    this.editableLocationFields.clear();
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  private locationAddressLine1(location: GeocodeResult, postalCode: string): string {
    if (location.addressLine1?.trim()) {
      return location.addressLine1.trim();
    }

    const streetAddress = this.stripStructuredAddressParts(location.formattedAddress, [
      location.area,
      location.city,
      location.state,
      postalCode,
      location.country,
    ]);

    return streetAddress || location.formattedAddress;
  }

  private stripStructuredAddressParts(
    address: string,
    values: Array<string | null | undefined>,
  ): string {
    const structuredParts = values
      .filter((part): part is string => Boolean(part?.trim()))
      .map((part) => this.normalizeAddressPart(part));
    return address
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => {
        const normalizedPart = this.normalizeAddressPart(part);
        return !structuredParts.some(
          (structuredPart) => normalizedPart === structuredPart || normalizedPart.includes(structuredPart),
        );
      })
      .join(', ');
  }

  private uniqueAddressParts(parts: Array<string | null | undefined>): string[] {
    const result: string[] = [];
    let combinedAddress = '';

    for (const value of parts) {
      const part = value?.trim();
      if (!part) {
        continue;
      }

      const normalizedPart = this.normalizeAddressPart(part);
      if (combinedAddress.includes(normalizedPart)) {
        continue;
      }

      result.push(part);
      combinedAddress = this.normalizeAddressPart(result.join(' '));
    }

    return result;
  }

  private normalizeAddressPart(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  private emptyForm(): CreateBusinessStoreRequest {
    return {
      name: '',
      code: '',
      addressLine1: '',
      addressLine2: '',
      area: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      phone: '',
      email: '',
      latitude: undefined,
      longitude: undefined,
      isPrimary: false,
    };
  }
}
